import { createContext, useContext, type RefObject } from "react";
import type { DocumentRules } from "@/lib/brokerDocumentTypes";
import type { SubmissionDetail } from "@/lib/submissions/brokerSubmissions";
import type { FileUpload } from "@/lib/useFileUploads";
import type {
  LossRuns,
  NoLossRunsReason,
  SubmissionDocument,
} from "@/lib/submissions/lossRuns";

// What a page's loss runs are, for the parts that show them (BRK-S361): the
// header's pill and button, the key fact, Review's and Preview's card, and
// the panel. `LossRunsProvider` holds it.

/** A file on its way up, or one that didn't make it, on its own row. */
export type LossRunUpload = FileUpload;

/** A file on record: a download or a removal under way, or why one didn't
 *  happen. */
export interface LossRunFileState {
  busy: "removing" | "downloading" | null;
  problem: string | null;
}

export interface LossRunsValue {
  submissionId: string;
  /** The insured's account, whose documents serve the downloads; null for a
   *  submission with no insured. */
  accountId: string | null;
  insured: string | null;
  submissionNumber: string;
  /** Bound (PMS-S708): its loss runs can be read and downloaded, no more. */
  locked: boolean;
  /** Where they stand: PMS's word, moved on only by a change PMS confirmed. */
  lossRuns: LossRuns;
  /** The files on record, oldest first; null until read. */
  documents: SubmissionDocument[] | null;
  /** Why the files couldn't be read, else null. */
  listProblem: string | null;
  /** Reads the files, unless a read is already under way. */
  readDocuments: () => void;
  uploads: readonly LossRunUpload[];
  /** Sends the chosen files, one a request, once PMS's served rules pass
   *  them here; those they don't stay on their rows, refused. */
  send: (files: readonly File[], rules: DocumentRules) => void;
  stop: (key: string) => void;
  retry: (key: string) => void;
  dismiss: (key: string) => void;
  fileState: (documentId: string) => LossRunFileState;
  remove: (documentId: string) => void;
  download: (document: SubmissionDocument) => void;
  /** Records why there are none; rejects with PMS's refusal. */
  record: (reason: NoLossRunsReason, note: string) => Promise<void>;
  /** Clears the recorded reason; rejects with PMS's refusal. */
  clear: () => Promise<void>;
  /** The panel, from the URL (`?lossRuns=1`). */
  open: boolean;
  show: () => void;
  close: () => void;
  /** The header's button, where focus goes back when the panel closes. */
  buttonRef: RefObject<HTMLButtonElement>;
}

export const LossRunsContext = createContext<LossRunsValue | null>(null);

/** The page's loss runs: null outside a provider, and while PMS hasn't said
 *  where they stand. */
export function useLossRuns(): LossRunsValue | null {
  return useContext(LossRunsContext);
}

/** Hands the provider a newer read of the submission than its own: the
 *  wizard's, after a save that may have moved its loss runs on (BRK-S389). */
export const LossRunsReadContext = createContext<
  ((read: SubmissionDetail) => void) | null
>(null);
